<template>
    <div class="verification-component">
        <DocumentPreview
            ref="preview"
            :product="product"
            :file="previewFile"
        />
        <UnclassifiedVerification
            v-if="showUnclassifiedVerification"
            :id="id"
            :product="product"
            :enable-polling="true"
            @refreshDocuments="$emit('refreshDocuments')"
        />
        <b-card
            no-body
            :border-variant="compliantStatementBorderVariant"
            v-if="isElementVisible('compliant_statement')"
        >
            <div class="card-header pr-2">
                <b-card-title>{{ $t('Component(Verification):compliant-statement-label') }}</b-card-title>
                <div class="demo-inline-spacing" v-if="!readOnly">
                    <b-button
                        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                        variant="primary"
                        class="btn-icon mt-0 mr-0"
                        @click="addCompliantStatement"
                    >
                        <feather-icon icon="PlusIcon" />
                    </b-button>
                </div>
            </div>
            <CompliantStatementTable
                :documents="compliantStatementDocuments"
                v-if="readOnly"
                @input="onDocumentSelected"
                @download="download"
                @preview="previewDocument"
            />
            <div
                v-else
                class="row pl-2 pr-2 document-row"
                v-for="(declaration, index) in compliantStatementDocuments"
            >
                <div class="col-lg-11">
                    <CompliantStatement
                        :legislations="legislations"
                        :declaration="declaration"
                        :compact-view="compactView"
                        @change="onCompliantStatementChange"
                        @download="download"
                        @preview="previewDocument"
                    />
                </div>
                <div class="col-lg-1" :class="{'mt-auto': !compactView, 'my-auto': compactView}" v-if="!readOnly">
                    <div class="text-right pb-1">
                        <b-button
                            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                            variant="danger"
                            class="btn-icon mt-0 mr-0"
                            @click="removeCompliantStatement(declaration, index)"
                        >
                            <feather-icon icon="MinusIcon" />
                        </b-button>
                    </div>
                </div>
            </div>
        </b-card>
        <b-card
            no-body
            :border-variant="docBorderVariant"
            v-if="isElementVisible('doc')"
        >
            <div class="card-header pr-2">
                <b-card-title>{{ $t('Component(Verification):doc-label') }}</b-card-title>
                <div class="demo-inline-spacing" v-if="!readOnly">
                    <b-button
                        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                        variant="primary"
                        class="btn-icon mt-0 mr-0"
                        @click="addDeclarationOfConformity"
                    >
                        <feather-icon icon="PlusIcon" />
                    </b-button>
                </div>
            </div>
            <DeclarationOfConformityTable
                v-if="readOnly"
                :documents="declarationsOfConformity"
                @input="onDocumentSelected"
                @download="download"
                @preview="previewDocument"
            />
            <div
                v-else
                class="row pl-2 pr-2 document-row"
                v-for="(declaration, index) in declarationsOfConformity"
            >
                <div class="col-lg-11">
                    <DeclarationOfConformity
                        :legislations="legislations"
                        :declaration="declaration"
                        :compact-view="compactView"
                        @change="onDeclarationChange"
                        @download="download"
                        @preview="previewDocument"
                    />
                </div>
                <div class="col-lg-1" :class="{'mt-auto': !compactView, 'my-auto': compactView}" v-if="!readOnly">
                    <div class="text-right pb-1">
                        <b-button
                            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                            variant="danger"
                            class="btn-icon mt-0 mr-0"
                            @click="removeDeclarationOfConformity(declaration, index)"
                        >
                            <feather-icon icon="MinusIcon" />
                        </b-button>
                    </div>
                </div>
            </div>
        </b-card>
        <b-card
            no-body
            :border-variant="evidenceReportBorderVariant"
            v-if="isElementVisible('evidence')"
        >
            <div class="card-header pr-2">
                <b-card-title>{{ $t('Component(Verification):evidence-report-label') }}</b-card-title>
                <div class="demo-inline-spacing" v-if="!readOnly">
                    <b-button
                        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                        variant="primary"
                        class="btn-icon mt-0 mr-0"
                        @click="addEvidenceReport"
                    >
                        <feather-icon icon="PlusIcon" />
                    </b-button>
                </div>
            </div>
            <EvidenceReportTable
                v-if="readOnly"
                :documents="evidenceReports"
                @input="onDocumentSelected"
                @download="download"
                @preview="previewDocument"
            />
            <div
                v-else
                class="row pl-2 pr-2 document-row"
                v-for="(report, index) in evidenceReports"
            >
                <div class="col-lg-11">
                    <EvidenceReport
                        :legislations="legislations"
                        :types="evidenceReportTypes"
                        :report="report"
                        :compact-view="compactView"
                        @change="onEvidenceReportChange"
                        @download="download"
                        @preview="previewDocument"
                    />
                </div>
                <div class="col-lg-1" :class="{'mt-auto': !compactView, 'my-auto': compactView}" v-if="!readOnly">
                    <div class="text-right pb-1">
                        <b-button
                            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                            variant="danger"
                            class="btn-icon mt-0 mr-0"
                            @click="removeEvidenceReport(report, index)"
                        >
                            <feather-icon icon="MinusIcon" />
                        </b-button>
                    </div>
                </div>
            </div>
        </b-card>
        <b-row v-if="readOnly">
            <b-col>
                <b-card-group deck>
                    <Instruction
                        :instruction="safeUse"
                        :title="$t('Component(Verification):safe-use-label')"
                        :state="safeUserBorderVariant"
                        :empty-text="getSafeUseEmptyText"
                    />
                    <Instruction
                        :instruction="disassembling"
                        :title="$t('Component(Verification):dissembling-label')"
                        :state="dissemblingBorderVariant"
                        @download="download"
                        @preview="previewDocument"
                    />
                </b-card-group>
            </b-col>
        </b-row>
        <div v-else>
            <b-card
                no-body
                :border-variant="safeUserBorderVariant"
                v-if="isElementVisible('safe_use')"
            >
                <div class="card-header">
                    <b-card-title>{{ $t('Component(Verification):safe-use-label') }}</b-card-title>
                </div>
                <b-alert
                    variant="light"
                    show
                    class="ml-2 mr-2"
                >
                    <div class="alert-body">
                        <span>{{ $t('Component(Verification):instruction-safe-use') }}</span>
                    </div>
                </b-alert>
                <div class="pl-2 pr-2 pb-2">
                    <b-form-checkbox
                        name="check-button"
                        v-model="isNotApplicable"
                        inline
                        @input="switchSafeUseApplicable"
                    >
                        {{ $t('Component(Text):safe-use-applicability') }}
                    </b-form-checkbox>
                </div>
                <div>
                    <div v-if="isSafeUseApplicable !== false && isElementVisible('safe_use')">
                        <b-form-group class="pl-2 pr-2" :label="$t('Component(Label):safe-use-instructions')">
                            <b-form-textarea
                                :placeholder="$t('Component(Placeholder):type-here')"
                                rows="3"
                                max-rows="8"
                                v-model="safeUseInstructionsDescription"
                                @input="safeUseChange"
                            />
                        </b-form-group>
                    </div>
                </div>
            </b-card>
            <b-card
                no-body
                :border-variant="dissemblingBorderVariant"
                v-if="isElementVisible('dissembling')"
            >
                <div class="card-header">
                    <b-card-title>{{ $t('Component(Verification):dissembling-label') }}</b-card-title>
                </div>
                <b-alert
                    variant="light"
                    show
                    class="ml-2 mr-2"
                >
                    <div class="alert-body">
                        <span>{{ $t('Component(Verification):instruction-disassembling') }}</span>
                    </div>
                </b-alert>
                <div>
                    <b-form-group class="pl-2 pr-2" :label="$t('Component(Label):dissembling-instructions')">
                        <b-form-textarea
                            :placeholder="$t('Component(Placeholder):type-here')"
                            rows="3"
                            max-rows="8"
                            v-model="disassemblingInstructionsDescription"
                            @input="disassemblingChange"
                        />
                    </b-form-group>
                    <InstructionDocumentFile
                        class="pl-2 pr-2"
                        :file="disassembling"
                        @input="disassemblingFileChange"
                        @download="download"
                        @preview="previewDocument"
                    />
                </div>
            </b-card>
        </div>
        <b-card
            v-if="otherDocuments.length"
            no-body
            border-variant="light"
        >
            <div class="card-header">
                <b-card-title>{{ $t('Component(VerificationDocumentTypeEnum):other') }}</b-card-title>
            </div>
            <div class="px-2 pb-1">
                <OtherVerificationDocumentRow
                    v-for="document in otherDocuments"
                    :key="document.id"
                    :document="document"
                    @download="download"
                    @preview="previewDocument"
                />
            </div>
        </b-card>
    </div>
</template>

<script>
import { BAlert, BCard, BCardTitle, BCardGroup, BRow, BCol, BFormGroup, BFormTextarea, BFormFile, BButton, BFormCheckbox } from 'bootstrap-vue'
import DeclarationOfConformity from "@/views/product/components/product-card/verification/DeclarationOfConformity.vue";
import EvidenceReport from "@/views/product/components/product-card/EvidenceReport";
import {LegislationApi} from "@core/api";
import Ripple from 'vue-ripple-directive'
import InstructionDocumentFile from "@/views/product/components/product-card/InstructionDocumentFile";
import DownloadProductDocument from "@core/mixins/download-product-document";
import {DocumentPreview} from "@core/components/product";
import CompliantStatement from "@/views/product/components/product-card/verification/CompliantStatement.vue";
import CompliantStatementTable from "@/views/product/components/product-card/verification/CompliantStatementTable.vue";
import {LegislationKey} from "@core/domain/legislation";
import DeclarationOfConformityTable
    from "@/views/product/components/product-card/verification/DeclarationOfConformityTable.vue";
import EvidenceReportTable from "@/views/product/components/product-card/verification/EvidenceReportTable.vue";
import Instruction from "@/views/product/components/product-card/verification/Instruction.vue";
import UnclassifiedVerification from "@/views/product/components/product-card/verification/UnclassifiedVerification.vue";
import OtherVerificationDocumentRow from "@/views/product/components/product-card/verification/OtherVerificationDocumentRow.vue";

export default {
    mixins: [DownloadProductDocument],
    components: {
        UnclassifiedVerification, OtherVerificationDocumentRow, Instruction,
        DeclarationOfConformityTable, EvidenceReportTable, CompliantStatementTable, CompliantStatement,
        InstructionDocumentFile, BAlert, DocumentPreview, BFormCheckbox, BCard, BButton, BCardTitle, BRow, BCol,
        BFormGroup, BFormTextarea, BFormFile, DeclarationOfConformity, EvidenceReport, BCardGroup
    },
    directives: {Ripple},
    props: {
        readOnly: {
            default: false
        },
        compactView: {
            type: Boolean,
            default: false,
        },
        product: {
            type: Object,
            default() {
                return {};
            }
        },
        id: {
            required: true
        },
        doc: {
            type: Array,
            default() {
                return [];
            }
        },
        compliantStatement: {
            type: Array,
            default() {
                return [];
            }
        },
        evidence: {
            type: Array,
            default() {
                return [];
            }
        },
        other: {
            type: Array,
            default() {
                return [];
            }
        },
        safeUse: {
            type: Object,
            default() {
                return {
                    safeUseInstructionsDescription: '',
                    safeUseInstructionsFile: null,
                    file: {
                        name: ''
                    },
                    id: null
                };
            }
        },
        disassembling: {
            type: Object,
            default() {
                return {
                    disassemblingInstructionsDescription: '',
                    disassemblingInstructionsFile: null,
                    file: {
                        name: ''
                    },
                    id: null
                };
            }
        },
        isSafeUseApplicable: {
            default: true
        },
        visibleElements: {
            type: Array,
            default() {
                return [
                    'doc',
                    'evidence',
                    'safe_use',
                    'dissembling',
                    'compliant_statement',
                    'unclassified_verification'
                ];
            }
        },
        docBorderVariant: {default: 'light'},
        compliantStatementBorderVariant: {default: 'light'},
        evidenceReportBorderVariant: {default: 'light'},
        safeUserBorderVariant: {default: 'light'},
        dissemblingBorderVariant: {default: 'light'},
        managePredefined: {
            default: true
        },
        requestedLegislations: {
            type: Array,
            default() {
                return [];
            }
        },
        defaultCompliantStatementLegislationList: {
            default() {
                return [
                    LegislationKey.REACH,
                    LegislationKey.SCIP,
                    LegislationKey.ROHS
                ];
            }
        },
        defaultEvidenceReportLegislationList: {
            default() {
                return [
                    LegislationKey.REACH,
                    LegislationKey.SCIP,
                    LegislationKey.ROHS
                ]
            }
        }
    },
    data() {
        return {
            legislations: [],
            apiLegislations: [],
            evidenceReportTypes: [],

            evidenceReports: [],
            declarationsOfConformity: [],
            compliantStatementDocuments: [],

            isNotApplicable: null,
            // safeUseData: this.safeUse,
            // disassemblingData: this.disassembling,
            safeUseInstructionsDescription: '',
            safeUseInstructionsFile: null,

            disassemblingInstructionsDescription: '',
            disassemblingInstructionsFile: null,

            //  HAM-152
            isDefaultDocVerificationManaged: false,
            isDefaultEvidenceVerificationManaged: false,
            isDefaultCompliantStatementManaged: false,

            //  preview
            previewFile: null
        };
    },
    methods: {
        onDocumentSelected(selected, document) {
            this.$emit('documentSelected', selected, document);
        },
        onDeclarationChange(declaration) {
            this.$emit('docChange', declaration);
        },
        onCompliantStatementChange(declaration) {
            this.$emit('compliantStatementChange', declaration);
        },
        onEvidenceReportChange(report) {
            this.$emit('evidenceReportChange', report);
        },
        download(file, done) {
            this
                .downloadProductDocument(this.product.id, file)
                .then(done);
        },

        previewDocument(file) {
            this.previewFile = file;
            this.$nextTick(() => {
                this.$refs.preview.show();
            });
        },

        switchSafeUseApplicable(isApplicable) {
            this.$emit('safeUseApplicableChange', !isApplicable)
        },

        makeDeclarationOfConformity() {
            return {
                legislations: this.getEUDOCPredefinedLegislations(),
                declarationDate: null,
                declarationValidityDate: null,
                document: null,
                id: null,
                file: {
                    name: ''
                }
            };
        },

        addDeclarationOfConformity() {
            this.$emit('addDoc', this.makeDeclarationOfConformity());
        },

        makeCompliantStatement() {
            return {
                legislations: this.getCompliantStatementDefaultLegislations(),
                declarationDate: null,
                declarationValidityDate: null,
                document: null,
                id: null,
                file: {
                    name: ''
                }
            };
        },

        addCompliantStatement() {
            this.$emit('addCompliantStatement', this.makeCompliantStatement());
        },

        removeDeclarationOfConformity(declaration, index) {
            this.$emit('removeDoc', declaration, index);
        },

        removeCompliantStatement(declaration, index) {
            this.$emit('removeCompliantStatement', declaration, index);
        },

        makeEvidenceReport(predefined = false) {
            return {
                legislations: this.getEvidenceReportPredefinedLegislations(),
                documentTypes: [],
                id: null,
                document: null,
                file: {
                    name: ''
                },
                predefined,
            };
        },

        addEvidenceReport(predefined = false) {
            this.$emit('addReport', this.makeEvidenceReport(predefined));
        },

        removeEvidenceReport(report, index) {
            this.$emit('removeReport', report, index)
        },

        safeUseChange() {
            this.$emit('safeUseChange', {
                description: this.safeUseInstructionsDescription,
                document: this.safeUseInstructionsFile,
                id: this.safeUse.id,
                file: this.safeUse.file
            });
        },

        safeUseFileChange(file) {
            this.safeUseInstructionsFile = file;
            this.safeUseChange();
        },

        disassemblingChange() {
            this.$emit('disassemblingChange', {
                description: this.disassemblingInstructionsDescription,
                document: this.disassemblingInstructionsFile,
                id: this.disassembling.id,
                file: this.disassembling.file
            });
        },
        disassemblingFileChange(file) {
            this.disassemblingInstructionsFile = file;
            this.disassemblingChange();
        },

        isElementVisible(element) {
            return this.visibleElements.includes(element);
        },

        mergeRequestedLegislations(apiLegislations, requestedLegislations) {
            const merged = new Map();

            apiLegislations.forEach((legislation) => {
                if (legislation && legislation.id !== undefined && legislation.id !== null) {
                    merged.set(legislation.id, legislation);
                }
            });

            requestedLegislations.forEach((legislation) => {
                if (legislation && legislation.id !== undefined && legislation.id !== null && !merged.has(legislation.id)) {
                    merged.set(legislation.id, legislation);
                }
            });

            return Array.from(merged.values());
        },

        refreshLegislations() {
            this.legislations = this.mergeRequestedLegislations(this.apiLegislations, this.requestedLegislations);
        },

        hydrateReferenceValues(values, options) {
            return (values || []).map(value => {
                const id = value && typeof value === 'object' ? value.id : value;
                const option = options.find(item => String(item.id) === String(id));

                if (option) {
                    return option;
                }

                if (value && typeof value === 'object') {
                    return {
                        ...value,
                        title: value.title || String(id),
                    };
                }

                return {
                    id,
                    title: String(id),
                };
            });
        },

        hydrateVerificationDocuments() {
            if (this.apiLegislations.length) {
                [...this.doc, ...this.compliantStatement, ...this.evidence].forEach(document => {
                    this.$set(document, 'legislations', this.hydrateReferenceValues(
                        document.legislations,
                        this.legislations
                    ));
                });
            }

            if (this.evidenceReportTypes.length) {
                this.evidence.forEach(document => {
                    this.$set(document, 'documentTypes', this.hydrateReferenceValues(
                        document.documentTypes,
                        this.evidenceReportTypes
                    ));
                });
            }
        },

        getEvidenceReportPredefinedLegislations() {
            return this.legislations.filter(l => {
                return this.defaultEvidenceReportLegislationList.includes(l.key)
                    || this.defaultEvidenceReportLegislationList.includes(l.id);
            });
        },

        getCompliantStatementDefaultLegislations() {
            return this.legislations.filter(l => {
                return this.defaultCompliantStatementLegislationList.includes(l.key)
                    || this.defaultCompliantStatementLegislationList.includes(l.id);
            });
        },

        getEUDOCPredefinedLegislations() {
            return this.legislations.filter(l => [
                LegislationKey.ROHS
            ].includes(l.key));
        },

        setPredefinedLegislationsForAutomaticallyAddedDocAndEvidence() {
            this.doc.forEach(d => {
                if (!d.id && !d.document && d.legislations.length === 0) {
                    d.legislations = this.getEUDOCPredefinedLegislations();
                }
            });

            this.evidence.forEach(d => {
                if (!d.id && !d.document && d.legislations.length === 0) {
                    d.legislations = this.getEvidenceReportPredefinedLegislations();
                }
            });

            this.compliantStatement.forEach(d => {
                if (!d.id && !d.document && d.legislations.length === 0) {
                    d.legislations = this.getCompliantStatementDefaultLegislations();
                }
            });
        }
    },

    computed: {
        otherDocuments() {
            return this.other.filter(document => document && document.id);
        },
        showUnclassifiedVerification() {
            return !this.readOnly
                && this.isElementVisible('unclassified_verification')
                && this.$store.getters.isUnclassifiedVerificationEnabled;
        },
        getSafeUseEmptyText() {
            if (false === this.isSafeUseApplicable) {
                return this.$t('Component(Text):safe-use-applicability')
            }

            return 'There is no Safe use instructions.';
        },
    },

    created() {
        this.declarationsOfConformity = this.doc;
        this.compliantStatementDocuments = this.compliantStatement;
        this.evidenceReports = this.evidence;
        setTimeout(() => {
            LegislationApi.all().then(r => {
                this.apiLegislations = r.data;
                this.refreshLegislations();
                this.hydrateVerificationDocuments();
                this.setPredefinedLegislationsForAutomaticallyAddedDocAndEvidence();
            });
            LegislationApi.getEvidenceReportTypes().then(r => {
                this.evidenceReportTypes = r.data;
                this.hydrateVerificationDocuments();
            });
        }, 1000);
    },

    watch: {
        doc(value) {
            this.declarationsOfConformity = value;
            this.hydrateVerificationDocuments();

            //  HAM-152
            if (this.managePredefined && 0 === value.length && false === this.isDefaultDocVerificationManaged) {
                this.isDefaultDocVerificationManaged = true;
                this.addDeclarationOfConformity();
            }
        },
        compliantStatement(value) {
            this.compliantStatementDocuments = value;
            this.hydrateVerificationDocuments();

            //  HAM-152
            if (this.managePredefined && 0 === value.length && false === this.isDefaultCompliantStatementManaged) {
                this.isDefaultCompliantStatementManaged = true;
                this.addCompliantStatement();
            }
        },
        evidence(value) {
            this.evidenceReports = value;
            this.hydrateVerificationDocuments();

            //  HAM-152
            if (this.managePredefined && 0 === value.length && false === this.isDefaultEvidenceVerificationManaged) {
                this.isDefaultEvidenceVerificationManaged = true;
                this.addEvidenceReport(true);
            }
        },
        safeUse(value) {
            this.safeUseInstructionsDescription = value.description;

            if (this.readOnly && value.id) {
                this.onDocumentSelected(true, value);
            }
        },
        disassembling(value) {
            this.disassemblingInstructionsDescription = value.description;

            if (this.readOnly && value.id) {
                this.onDocumentSelected(true, value);
            }
        },
        isSafeUseApplicable(value) {
            this.isNotApplicable = value === null ? false : !value;
        },
        requestedLegislations: {
            deep: true,
            handler() {
                this.refreshLegislations();
            }
        }
    }
}
</script>
